'use client';

import clsx from 'clsx';
import Timer from '@/app/ui/wallet/Timer/Timer';
import BuyForm from '@/app/ui/wallet/BuyForm';
import { useAccount, useDisconnect } from 'wagmi';
import Icon from '@/app/ui/icons/Icon';
import { toast } from 'react-toastify';
import useWidget from '@/app/hooks/useWidget';
import ProgressBar from '@/app/ui/wallet/ProgressBar';
import Stats from '@/app/ui/wallet/Stats';
import dynamic from 'next/dynamic';

const Button = dynamic(() => import('@/app/ui/nav/Button'), { ssr: false });

function shortenString(str: string) {
  if (str.length > 12) {
    return `${str.slice(0, 6)}...${str.slice(-6)}`;
  }
  return str;
}

export default function CryptoWidget() {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { data } = useWidget();
  return (
    <div className="z-20 w-[400px]">
      {address && (
        <div className="mb-2 ml-auto flex justify-end gap-2">
          <Button
            onClick={() => {
              navigator.clipboard.writeText(address).then(() => {
                toast('Copied!', { type: 'success' });
              });
            }}
            className="quiroh-font relative flex h-[35px] max-w-[150px] items-center justify-center rounded-[8px] bg-[#21359B] px-[15px] text-[#AAD4F9] duration-500 hover:bg-[#3552c9]"
          >
            {shortenString(address)}
          </Button>
          <Button
            onClick={() => disconnect()}
            className="flex size-[35px] items-center justify-center rounded-[8px] bg-[#21359B] text-[#AAD4F9] duration-500 hover:bg-[#3552c9]"
          >
            <Icon name="logout" width={16} height={16} viewBox="0 0 14 14" />
          </Button>
        </div>
      )}
      <div
        className={`blur-25 z-20 flex min-h-[707px] w-full flex-col items-center justify-center overflow-hidden rounded-[26.74px] bg-widget-background px-4 pb-4 ${isConnected ? 'pt-[10px]' : 'pt-5'}`}
      >
        <div
          className={clsx(
            'flex w-full flex-col items-center justify-center rounded-[17px]',
            {
              'mb-[16px] bg-widget-info-background px-[16px] pb-[16px] pt-[7px] backdrop-blur-[64px]':
              isConnected,
              'mb-[21px] bg-transparent': !isConnected
            }
          )}
        >
          {data?.nextPriceJump > 0 && (
            <>
              <p className="mb-4 text-[25px] text-pale-mist">
                Next price increase in
              </p>
              <Timer />
            </>
          )}
          <div
            className={clsx('w-full rounded-[18px]', {
              'bg-transparent': isConnected,
              'bg-widget-info-background p-4 backdrop-blur-[64px]':
                !isConnected
            })}
          >
            {!isConnected && (
              <p className="mb-5 text-center text-[21px] text-pale-mist">
                Until Price Increase
              </p>
            )}
            <ProgressBar />
            <Stats />
          </div>
        </div>
        <div
          className={`flex flex-col items-center ${isConnected ? 'mb-[15px] text-[14px]' : 'mb-5 text-base'}`}
        >
          <div className="flex gap-2">
            <p className="quiroh-font text-[rgba(170,212,249,0.8)]">
              Your stakeable
            </p>
            <p className="text-baby-blue">
              $GOIN <span className="text-[rgba(170,212,249,0.6)]">= </span>
              <span className="text-pale-mist">{data?.stakeable ?? 0}</span>
            </p>
          </div>
        </div>
        <div
          className={`${isConnected ? 'mb-[19px]' : 'mb-6'} flex w-full justify-center rounded-[33px] bg-widget-info-background py-2 backdrop-blur-[46px]`}
        >
          <p className="text-baby-blue">
            1 $GOIN <span className="text-[rgba(170,212,249,0.6)]">=</span>{' '}
            <span className="text-pale-mist">{`${data?.pricePerUnit ?? 0}$`}</span>
          </p>
        </div>
        <BuyForm />
      </div>
    </div>
  );
}
